import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, LogOut, Bell, Heart, ChevronRight, Sparkles, Loader2, Dog, ExternalLink, CreditCard, Plus, Trash2, Mail, MessageSquare, Send, Smartphone, ClipboardList, Pencil, User } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';
import { PARENT_PLAN, PAWPAL_URL } from '../../mock';
import { enablePush, disablePush } from '../../push';
import ReferralCard from '../../components/ReferralCard';
import ChangePassword from '../../components/ChangePassword';
import BreedTips from '../../components/BreedTips';
import api from '../../api';

export default function Profile() {
  const nav = useNavigate();
  const { cats, selectedCat, selectedCatId, chooseCat, removeCat, user, logout, deleteAccount, updateNotifPrefs, updateProfile, setUser } = useApp();
  const cat = selectedCat;
  const hasDog = typeof window !== 'undefined' && localStorage.getItem('pp_has_dog') === 'true';

  const [pName, setPName] = useState(user?.name || '');
  const [pEmail, setPEmail] = useState(user?.email || '');
  const [pPhone, setPPhone] = useState(user?.phone || '');
  const [detailsBusy, setDetailsBusy] = useState(false);
  const detailsDirty = pName !== (user?.name || '') || pEmail !== (user?.email || '') || pPhone !== (user?.phone || '');

  const saveDetails = async () => {
    if (!pEmail.trim()) { toast.error('Email cannot be empty'); return; }
    setDetailsBusy(true);
    try {
      await updateProfile({ name: pName.trim(), email: pEmail.trim(), phone: pPhone.trim() });
      toast.success('Your details were saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save details'); }
    finally { setDetailsBusy(false); }
  };

  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const initPrefs = { email_enabled: false, email: user?.email || '', sms_enabled: false, phone: '', push_enabled: false, ...(user?.notif_prefs || {}) };
  const [prefs, setPrefs] = useState(initPrefs);
  const [prefBusy, setPrefBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [otpStep, setOtpStep] = useState('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);

  const phoneVerified = !!prefs.verified_phone && !!prefs.phone_verified && prefs.phone === prefs.verified_phone;

  const sendCode = async () => {
    if (!prefs.phone?.trim()) { toast.error('Enter your phone number first'); return; }
    setOtpBusy(true);
    try {
      const r = await api.post('/notifications/phone/start', { phone: prefs.phone });
      setPrefs((p) => ({ ...p, phone: r.data.phone }));
      setOtpStep('sent');
      toast.success('Verification code sent by text');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send code'); }
    finally { setOtpBusy(false); }
  };

  const verifyCode = async () => {
    setOtpBusy(true);
    try {
      const r = await api.post('/notifications/phone/verify', { phone: prefs.phone, code: otpCode });
      setPrefs((p) => ({ ...p, ...r.data.notif_prefs }));
      setUser((u) => ({ ...u, notif_prefs: r.data.notif_prefs }));
      setOtpStep('idle'); setOtpCode('');
      toast.success('Phone verified — SMS alerts are on');
    } catch (e) { toast.error(e?.response?.data?.detail || 'That code is incorrect'); }
    finally { setOtpBusy(false); }
  };

  const changeNumber = () => {
    setPrefs((p) => ({ ...p, phone_verified: false, verified_phone: '' }));
    setOtpStep('idle'); setOtpCode('');
  };

  const onDelete = async () => {
    if (!window.confirm('Delete your account and all data (cats, chat history, records)? This cannot be undone.')) return;
    await deleteAccount();
    toast.success('Your account and data were deleted');
    nav('/');
  };

  const onRemoveCat = async () => {
    if (cats.length <= 1) { toast.error('You need at least one cat. Add another first.'); return; }
    if (!window.confirm(`Remove ${cat?.name} and their reminders?`)) return;
    await removeCat(cat.id);
    toast.success('Cat removed');
  };

  const savePrefs = async () => {
    setPrefBusy(true);
    try { await updateNotifPrefs(prefs); toast.success('Notification preferences saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setPrefBusy(false); }
  };

  const sendTest = async () => {
    try { const r = await api.post('/notifications/test'); const ch = Object.keys(r.data.results || {}); toast.success(ch.length ? `Test sent via ${ch.join(' & ')}` : 'Test triggered'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Enable email, SMS or push first'); }
  };

  const onTogglePush = async (e) => {
    const on = e.target.checked;
    setPushBusy(true);
    try {
      if (on) {
        await enablePush();
        const np = { ...prefs, push_enabled: true };
        setPrefs(np);
        await updateNotifPrefs(np);
        toast.success('Push notifications enabled');
      } else {
        try { await disablePush(); } catch (_) {}
        const np = { ...prefs, push_enabled: false };
        setPrefs(np);
        await updateNotifPrefs(np);
        toast.info('Push notifications disabled');
      }
    } catch (err) {
      const m = err.message === 'denied' ? 'Notifications are blocked in your browser settings'
        : err.message === 'no-sw' ? 'Install Purrflect to your home screen first to enable push'
        : err.message === 'unsupported' ? 'Push is not supported on this browser'
        : 'Could not enable push. Please try again.';
      toast.error(m);
    } finally { setPushBusy(false); }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post('/payments/portal', { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Billing portal unavailable right now.');
      setPortalBusy(false);
    }
  };

  const checkout = async (lookup_key) => {
    setBusy(true);
    try {
      const r = await api.post('/payments/checkout', { lookup_key, origin_url: window.location.origin, user_id: user?.id });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error('Could not start checkout. Please try again.'); setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
        <h1 className="text-base font-extrabold">Profile</h1>
      </header>

      <div className="px-4 py-4">
        {/* Your details */}
        <div className="mb-4 rounded-2xl bg-white p-4 card-soft" data-testid="your-details">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent"><User className="h-4.5 w-4.5 text-[hsl(var(--coral))]" /></div>
            <h2 className="font-extrabold">Your details</h2>
          </div>
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Name</label>
              <input value={pName} onChange={(e) => setPName(e.target.value)} placeholder="Your name" data-testid="profile-name" className="mt-1 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Email (login)</label>
              <input value={pEmail} onChange={(e) => setPEmail(e.target.value)} type="email" inputMode="email" data-testid="profile-email" className="mt-1 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
              <p className="mt-1 text-[11px] text-muted-foreground">Changing your email also changes the email address you use to sign in.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Phone <span className="font-normal">(contact only)</span></label>
              <input value={pPhone} onChange={(e) => setPPhone(e.target.value)} inputMode="tel" placeholder="+1 555 123 4567" data-testid="profile-phone" className="mt-1 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
              <p className="mt-1 text-[11px] text-muted-foreground">To receive text alerts, set and verify your number under Reminder notifications below.</p>
            </div>
            <button onClick={saveDetails} disabled={detailsBusy || !detailsDirty} data-testid="save-details-btn" className="coral-btn flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white disabled:opacity-50">
              {detailsBusy && <Loader2 className="h-4 w-4 animate-spin" />}Save details
            </button>
          </div>
        </div>

        {/* Cat switcher */}
        {cats.length > 1 && (
          <div className="mb-3 flex gap-2 overflow-x-auto no-scrollbar">
            {cats.map((c) => (
              <button key={c.id} onClick={() => chooseCat(c.id)} className={`flex shrink-0 items-center gap-2 rounded-full border-2 py-1.5 pl-1.5 pr-3 transition ${((selectedCatId || cats[0]?.id) === c.id) ? 'border-[hsl(var(--coral))] bg-accent/40' : 'border-border bg-white'}`}>
                <img src={c.avatar} alt={c.name} className="h-7 w-7 rounded-full object-cover" />
                <span className="text-sm font-bold">{c.name}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-4 rounded-2xl bg-white p-4 card-soft">
          <img src={cat?.avatar} alt={cat?.name} className="h-16 w-16 rounded-2xl object-cover" />
          <div className="flex-1">
            <h2 className="text-xl font-extrabold">{cat?.name}</h2>
            <p className="text-sm text-muted-foreground">{cat?.breed}{cat?.age ? ` · ${cat.age} yrs` : ''}</p>
          </div>
          {cats.length > 1 && <button onClick={onRemoveCat} className="p-2 text-muted-foreground hover:text-destructive" data-testid="remove-cat-btn"><Trash2 className="h-4 w-4" /></button>}
          <button onClick={() => nav(`/app/edit-cat/${cat?.id}`)} data-testid="edit-cat-btn" className="p-2 text-muted-foreground hover:text-[hsl(var(--coral))]"><Pencil className="h-4 w-4" /></button>
        </div>
        <button onClick={() => nav('/app/add-cat')} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-white py-3 text-sm font-bold text-[hsl(var(--coral))] hover:border-[hsl(var(--coral))]">
          <Plus className="h-4 w-4" />Add another cat
        </button>

        <BreedTips cat={cat} />

        <ReferralCard />

        {!user?.is_pro ? (
          <div className="mt-5 overflow-hidden rounded-3xl bg-white card-soft">
            <div className="bg-[hsl(var(--coral))] px-5 py-4 text-white">
              <div className="flex items-center gap-2"><Crown className="h-5 w-5" /><span className="font-extrabold">Purrflect PRO</span></div>
              <p className="mt-1 text-sm text-white/90">Unlock unlimited care for {cat?.name}.</p>
            </div>
            <div className="px-5 py-4">
              <p><span className="text-4xl font-extrabold text-[hsl(var(--coral))]">${PARENT_PLAN.price}</span><span className="text-muted-foreground">/month</span></p>
              <p className="text-xs text-muted-foreground">or ${PARENT_PLAN.bundlePrice}/mo for both Purrflect + Wooflect</p>
              <ul className="mt-3 space-y-2 text-sm">
                {PARENT_PLAN.perks.map((p) => <li key={p} className="flex items-center gap-2"><Check className="h-4 w-4 text-[hsl(var(--coral))]" />{p}</li>)}
              </ul>
              <button onClick={() => checkout('purrpal_parent_pro')} disabled={busy} className="coral-btn mt-4 flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-base font-bold text-white disabled:opacity-70">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />}Upgrade to PRO – ${PARENT_PLAN.price}/mo
              </button>
              <button onClick={() => checkout('purrpal_parent_bundle')} disabled={busy} className="mt-2 w-full rounded-2xl border border-border bg-white py-3 text-sm font-bold text-foreground">
                Get both apps (Purrflect + Wooflect) – ${PARENT_PLAN.bundlePrice}/mo
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 flex flex-col gap-3 rounded-2xl bg-accent/60 p-4">
            <div className="flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-[hsl(var(--coral))]" />
              <div><p className="font-extrabold">Purrflect PRO active</p><p className="text-sm text-muted-foreground">{user?.plan === 'bundle' ? 'Both apps unlocked.' : 'Enjoy unlimited care.'} Cancel anytime.</p></div>
            </div>
            <button onClick={openPortal} disabled={portalBusy} className="flex w-full items-center justify-center gap-2 rounded-xl border border-[hsl(var(--coral))] bg-white py-2.5 text-sm font-bold text-[hsl(var(--coral))] disabled:opacity-70">
              {portalBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}Manage billing
            </button>
          </div>
        )}

        <div className="mt-5 overflow-hidden rounded-2xl bg-white card-soft">
          {[{ icon: Bell, label: 'Care reminders', to: '/app/reminders' }, { icon: Heart, label: 'Health history', to: null }].map((r) => (
            <button key={r.label} onClick={() => r.to ? nav(r.to) : toast.info('Coming soon in this preview')} className="flex w-full items-center gap-3 border-b border-border px-4 py-3.5 text-left last:border-0 hover:bg-secondary/40">
              <r.icon className="h-5 w-5 text-muted-foreground" /><span className="flex-1 font-semibold">{r.label}</span><ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>

        {/* Reminder notifications (PRO) */}
        {user?.is_pro && (
          <div className="mt-5 rounded-2xl bg-white p-4 card-soft">
            <div className="flex items-center gap-2"><Bell className="h-5 w-5 text-[hsl(var(--coral))]" /><h3 className="font-extrabold">Reminder notifications</h3></div>
            <p className="mt-1 text-xs text-muted-foreground">We'll notify you <span className="font-semibold text-foreground">7 days</span> and again <span className="font-semibold text-foreground">3 days</span> before each reminder.</p>

            <label className="mt-4 flex items-center gap-3">
              <input type="checkbox" checked={prefs.email_enabled} onChange={(e) => setPrefs({ ...prefs, email_enabled: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--coral))]" />
              <Mail className="h-4 w-4 text-muted-foreground" /><span className="flex-1 text-sm font-semibold">Email</span>
            </label>
            {prefs.email_enabled && (
              <input value={prefs.email} onChange={(e) => setPrefs({ ...prefs, email: e.target.value })} placeholder="you@example.com" className="mt-2 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            )}

            <label className="mt-3 flex items-center gap-3">
              <input type="checkbox" checked={prefs.sms_enabled} onChange={(e) => setPrefs({ ...prefs, sms_enabled: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--coral))]" data-testid="sms-toggle" />
              <MessageSquare className="h-4 w-4 text-muted-foreground" /><span className="flex-1 text-sm font-semibold">Text message (SMS)</span>
              {phoneVerified && <span className="flex items-center gap-1 text-xs font-bold text-green-600" data-testid="sms-verified-badge"><Check className="h-3.5 w-3.5" />Verified</span>}
            </label>
            {prefs.sms_enabled && (
              <div className="mt-2">
                {phoneVerified ? (
                  <div className="flex items-center justify-between rounded-xl border border-green-200 bg-green-50 px-3 py-2.5 text-sm">
                    <span className="font-semibold text-green-800" data-testid="sms-verified-phone">{prefs.phone}</span>
                    <button onClick={changeNumber} data-testid="sms-change-number" className="text-xs font-bold text-[hsl(var(--coral))] underline">Change number</button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input value={prefs.phone} onChange={(e) => setPrefs({ ...prefs, phone: e.target.value })} placeholder="+1 555 000 0000" disabled={otpStep === 'sent'} data-testid="sms-phone-input" className="h-11 flex-1 rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))] disabled:bg-secondary/40" />
                      <button onClick={sendCode} disabled={otpBusy} data-testid="sms-send-code" className="flex h-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(var(--coral))] bg-white px-3 text-sm font-bold text-[hsl(var(--coral))] disabled:opacity-60">
                        {otpBusy && otpStep === 'idle' ? <Loader2 className="h-4 w-4 animate-spin" /> : (otpStep === 'sent' ? 'Resend' : 'Send code')}
                      </button>
                    </div>
                    {otpStep === 'sent' && (
                      <div className="mt-2 flex gap-2">
                        <input value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} inputMode="numeric" placeholder="6-digit code" data-testid="sms-otp-input" className="h-11 flex-1 rounded-xl border border-input bg-white px-3 text-sm tracking-widest outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
                        <button onClick={verifyCode} disabled={otpBusy || otpCode.length !== 6} data-testid="sms-verify-code" className="coral-btn flex h-11 shrink-0 items-center justify-center rounded-xl px-4 text-sm font-bold text-white disabled:opacity-60">
                          {otpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                        </button>
                      </div>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">We'll text a 6-digit code to confirm your number before turning on SMS alerts.</p>
                  </>
                )}
              </div>
            )}

            <label className="mt-3 flex items-center gap-3" data-testid="push-toggle-label">
              <input type="checkbox" checked={!!prefs.push_enabled} disabled={pushBusy} onChange={onTogglePush} className="h-4 w-4 accent-[hsl(var(--coral))]" data-testid="push-toggle-input" />
              <Smartphone className="h-4 w-4 text-muted-foreground" /><span className="flex-1 text-sm font-semibold">Push notifications</span>
              {pushBusy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </label>
            {prefs.push_enabled && (
              <p className="mt-1 pl-7 text-xs text-muted-foreground">Alerts pop up on your phone even when the app is closed. Works best from the installed app.</p>
            )}

            <div className="mt-4 flex gap-2">
              <button onClick={savePrefs} disabled={prefBusy} className="coral-btn flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-70">{prefBusy && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
              <button onClick={sendTest} className="flex items-center justify-center gap-1.5 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-bold"><Send className="h-4 w-4" />Test</button>
            </div>
          </div>
        )}

        {/* Sister-app cross promo */}
        <div className={`mt-5 overflow-hidden rounded-2xl card-soft ${hasDog ? 'bg-accent/50 border-2 border-[hsl(var(--coral))]' : 'bg-white border border-border'}`}>
          <div className="flex items-start gap-3 p-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent">
              <Dog className="h-5 w-5 text-[hsl(var(--coral))]" />
            </div>
            <div className="flex-1">
              <p className="font-extrabold">{hasDog ? 'You have a dog too!' : 'Have a dog too?'}</p>
              <p className="text-sm text-muted-foreground">Meet <span className="font-semibold text-foreground">Wooflect</span>, our sister app for dogs. {user?.is_pro && user?.plan === 'bundle' ? 'Your bundle already includes it.' : `Bundle both apps for just $${PARENT_PLAN.bundlePrice}/mo.`}</p>
              <a href={PAWPAL_URL} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--coral))] px-4 py-2 text-sm font-bold text-white">
                Open Wooflect <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Contact / gallery submission */}
        <div className="mt-5 rounded-2xl bg-white p-4 card-soft" data-testid="contact-note">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent">
              <Mail className="h-5 w-5 text-[hsl(var(--coral))]" />
            </div>
            <div className="flex-1">
              <p className="font-extrabold">We'd love to hear from you</p>
              <p className="mt-1 text-sm text-muted-foreground">Have a suggestion, comment, or need support? Want your pet featured in our gallery? Send us a note and a photo of your kitty for happybigdogs.net.</p>
              <a
                href="mailto:BigDog@happybigdogs.net?subject=Purrflect%20%E2%80%94%20Suggestion%2FSupport%2FGallery&body=Hi%20Purrflect%20team%2C%0A%0A(Tell%20us%20your%20suggestion%2C%20comment%2C%20or%20support%20need%20%E2%80%94%20and%20feel%20free%20to%20attach%20a%20photo%20of%20your%20cat%20for%20the%20gallery!)%0A%0AThanks%2C"
                data-testid="contact-us-btn"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--coral))] px-4 py-2 text-sm font-bold text-white"
              >
                Contact us <Mail className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {user?.is_admin && (
          <button onClick={() => nav('/admin')} data-testid="admin-link" className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-neutral-900 py-3.5 text-sm font-bold text-white">
            <ClipboardList className="h-4 w-4" />Open Leads Dashboard
          </button>
        )}

        <ChangePassword />

        <button onClick={() => { logout(); toast.success('Signed out'); nav('/'); }} className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-white py-3.5 text-sm font-bold text-destructive"><LogOut className="h-4 w-4" />Sign out</button>
        <button onClick={onDelete} className="mt-3 w-full text-center text-xs font-semibold text-muted-foreground underline underline-offset-2">Delete my account &amp; data</button>
        <p className="mt-3 text-center text-xs text-muted-foreground">Signed in as {user?.email || 'Guest'}</p>
        <div className="mt-4 flex items-center justify-center gap-3 text-xs text-muted-foreground">
          <a href="/terms" className="underline underline-offset-2">Terms</a>
          <span>·</span>
          <a href="/privacy" className="underline underline-offset-2">Privacy</a>
          <span>·</span>
          <a href="/disclaimer" className="underline underline-offset-2">Disclaimer</a>
          <span>·</span>
          <a href="/sms-optin" className="underline underline-offset-2" data-testid="profile-sms-optin-link">SMS Alerts</a>
        </div>
      </div>
    </div>
  );
}
